import { Suspense, lazy } from "react";
import { Navigate, useRoutes, useLocation } from "react-router-dom";
// layouts
import MainLayout from "../layouts/main";
import DashboardLayout from "../layouts/dashboard";
import LogoOnlyLayout from "../layouts/LogoOnlyLayout";
// guards
import GuestGuard from "../guards/GuestGuard";
import AuthGuard from "../guards/AuthGuard";
import PermGuard from "../guards/PermGuard";
import { PERM } from "../guards/perms";
// config
import { PATH_AFTER_LOGIN } from "../config";
// components
import LoadingScreen from "../components/LoadingScreen";

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense
      fallback={<LoadingScreen isDashboard={pathname.includes("/dashboard")} />}
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: "auth",
      children: [
        {
          path: "login",
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        { path: "reset-password", element: <ResetPassword /> },
        { path: "verify", element: <VerifyCode /> },
      ],
    },

    // Dashboard Routes
    {
      path: "dashboard",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: "home", element: <GeneralApp /> },
        // configurations
        {
          path: "configuration-transaction",
          children: [
            {
              element: (
                <Navigate
                  to="/dashboard/configuration-transaction/exchange-rate"
                  replace
                />
              ),
              index: true,
            },
            {
              path: "exchange-rate",
              element: (
                <PermGuard perm={PERM.CONFIG_EXCHANGE_RATE_VIEW}>
                  <ExchangeRateList />
                </PermGuard>
              ),
            },
            {
              path: "exchange-rate/new",
              element: (
                <PermGuard perm={PERM.CONFIG_EXCHANGE_RATE_ADD}>
                  <ExchangeRateCreate />
                </PermGuard>
              ),
            },
            {
              path: "exchange-rate/:exchangeRateKey/edit",
              element: (
                <PermGuard perm={PERM.CONFIG_EXCHANGE_RATE_CHANGE}>
                  <ExchangeRateCreate />
                </PermGuard>
              ),
            },

            {
              path: "fee",
              element: (
                <PermGuard perm={PERM.CONFIG_FEE_VIEW}>
                  <FeeList />
                </PermGuard>
              ),
            },
            {
              path: "fee/new",
              element: (
                <PermGuard perm={PERM.CONFIG_FEE_ADD}>
                  <FeeCreate />
                </PermGuard>
              ),
            },
            {
              path: "fee/:feeKey/edit",
              element: (
                <PermGuard perm={PERM.CONFIG_FEE_CHANGE}>
                  <FeeCreate />
                </PermGuard>
              ),
            },

            {
              path: "user-commission",
              element: (
                <PermGuard perm={PERM.CONFIG_USER_COMMISSION_VIEW}>
                  <UserCommissionList />
                </PermGuard>
              ),
            },
            {
              path: "user-commission/new",
              element: (
                <PermGuard perm={PERM.CONFIG_USER_COMMISSION_ADD}>
                  <UserCommissionCreate />
                </PermGuard>
              ),
            },
            {
              path: "user-commission/:userCommissionKey/edit",
              element: (
                <PermGuard perm={PERM.CONFIG_USER_COMMISSION_CHANGE}>
                  <UserCommissionCreate />
                </PermGuard>
              ),
            },
          ],
        },
        {
          path: "security-and-limit",
          children: [
            {
              element: (
                <Navigate
                  to="/dashboard/security-and-limit/limit-transaction-generale"
                  replace
                />
              ),
              index: true,
            },
            {
              path: "limit-transaction-generale",
              element: (
                <PermGuard perm={PERM.CONFIG_LIMIT_GENERAL_VIEW}>
                  <LimitTransactionGeneraleList />
                </PermGuard>
              ),
            },
            {
              path: "limit-transaction-generale/new",
              element: (
                <PermGuard perm={PERM.CONFIG_LIMIT_GENERAL_ADD}>
                  <LimitTransactionGeneraleCreate />
                </PermGuard>
              ),
            },
            {
              path: "limit-transaction-generale/:limitTransactionKey/edit",
              element: (
                <PermGuard perm={PERM.CONFIG_LIMIT_GENERAL_CHANGE}>
                  <LimitTransactionGeneraleCreate />
                </PermGuard>
              ),
            },
          ],
        },
        // supervisors
        {
          path: "master-agent",
          children: [
            {
              element: <Navigate to="/dashboard/master-agent/list" replace />,
              index: true,
            },
            {
              path: "list",
              element: (
                <PermGuard perm={PERM.SUPERVISOR_LIST}>
                  <SupervisorList />
                </PermGuard>
              ),
            },
            {
              path: "new",
              element: (
                <PermGuard perm={PERM.SUPERVISOR_CREATE}>
                  <SupervisorCreate />
                </PermGuard>
              ),
            },
            {
              path: ":supervisorKey/edit",
              element: (
                <PermGuard perm={PERM.SUPERVISOR_CREATE}>
                  <SupervisorCreate />
                </PermGuard>
              ),
            },
          ],
        },
        {
          path: "agent",
          children: [
            {
              element: <Navigate to="/dashboard/agent/list" replace />,
              index: true,
            },
            {
              path: "list",
              element: (
                <PermGuard perm={PERM.VENDOR_LIST}>
                  <AgentList />
                </PermGuard>
              ),
            },
            {
              path: "new",
              element: (
                <PermGuard perm={PERM.VENDOR_CREATE}>
                  <AgentCreate />
                </PermGuard>
              ),
            },
            {
              path: ":agentKey/edit",
              element: (
                <PermGuard perm={PERM.VENDOR_CREATE}>
                  <AgentCreate />
                </PermGuard>
              ),
            },
          ],
        },
        {
          path: "ticket",
          children: [
            {
              element: <Navigate to="/dashboard/ticket/sale" replace />,
              index: true,
            },
            {
              path: "sale",
              element: (
                <PermGuard perm={PERM.TICKET_SALE}>
                  <Tickets />
                </PermGuard>
              ),
            },
            {
              path: "cancel",
              element: (
                <PermGuard perm={PERM.TICKET_CANCEL}>
                  <TicketCancel />
                </PermGuard>
              ),
            },
            {
              path: "edit",
              element: (
                <PermGuard perm={PERM.TICKET_EDIT}>
                  <TicketEdit />
                </PermGuard>
              ),
            },
            {
              path: "sale/:id",
              element: (
                <PermGuard perm={PERM.TICKET_SALE}>
                  <TicketDetails />
                </PermGuard>
              ),
            },
            {
              path: "sale/:id/detail",
              element: (
                <PermGuard perm={PERM.TICKET_SALE}>
                  <TicketTransacDetails />
                </PermGuard>
              ),
            },
          ],
        },
        // reports
        {
          path: "report",
          children: [
            {
              element: <Navigate to="/dashboard/report/sale" replace />,
              index: true,
            },
            {
              path: "sale",
              element: (
                <PermGuard perm={PERM.REPORT_SALE}>
                  <Report />
                </PermGuard>
              ),
            },
            {
              path: "transactions",
              element: (
                <PermGuard perm={PERM.TRANSACTIONS_LIST}>
                  <TransactionList />
                </PermGuard>
              ),
            },

            {
              path: "commission",
              element: (
                <PermGuard perm={PERM.COMMISSION_PAY}>
                  <CommissionPage />
                </PermGuard>
              ),
            },
          ],
        },
        {
          path: "user",
          children: [
            {
              element: <Navigate to="/dashboard/user/account" replace />,
              index: true,
            },
            { path: "account", element: <UserAccount /> },
          ],
        },
      ],
    },

    // Main Routes
    {
      path: "*",
      element: <LogoOnlyLayout />,
      children: [
        { path: "politique-confidentialite", element: <PolitiqueConf /> },
        { path: "termes-conditions", element: <TermesConditions /> },
        {
          path: "politique-securite",
          element: <PolitiqueSecurite />,
        },
        { path: "coming-soon", element: <ComingSoon /> },
        { path: "maintenance", element: <Maintenance /> },
        { path: "500", element: <Page500 /> },
        { path: "404", element: <NotFound /> },
        { path: "*", element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: "/",
      element: <MainLayout />,
      children: [
        {
          element: <Navigate to="/dashboard" replace />,
          index: true,
        },
        // {
        //   element: (
        //     <GuestGuard>
        //       <HomePage />
        //     </GuestGuard>
        //   ),
        //   index: true,
        // },
        // { path: "about-us", element: <About /> },
        // { path: "contact-us", element: <Contact /> },
        // { path: "faqs", element: <Faqs /> },
      ],
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import("../pages/auth/Login")));
const ResetPassword = Loadable(
  lazy(() => import("../pages/auth/ResetPassword"))
);
const VerifyCode = Loadable(lazy(() => import("../pages/auth/VerifyCode")));

// DASHBOARD

// GENERAL
const GeneralApp = Loadable(
  lazy(() => import("../pages/dashboard/GeneralApp"))
);

// TICKET SALE
const Tickets = Loadable(lazy(() => import("../pages/dashboard/Tickets")));

const TicketDetails = Loadable(
  lazy(() => import("../pages/dashboard/TicketDetails"))
);
const TicketTransacDetails = Loadable(
  lazy(() => import("../pages/dashboard/TicketTransacDetails"))
);
const TicketCancel = Loadable(
  lazy(() => import("../pages/dashboard/TicketCancel"))
);
const TicketEdit = Loadable(
  lazy(() => import("../pages/dashboard/TicketEdit"))
);

// REPORT
const Report = Loadable(
  lazy(() => import("../pages/dashboard/Report/ReportInit"))
);
const TransactionList = Loadable(
  lazy(() => import("../pages/dashboard/TransactionList"))
);
const CommissionPage = Loadable(
  lazy(() => import("../pages/dashboard/CommissionPage"))
);


// EXCHANGE RATE
const ExchangeRateList = Loadable(
  lazy(() => import("../pages/dashboard/ExchangeRateList"))
);

const ExchangeRateCreate = Loadable(
  lazy(() => import("../pages/dashboard/ExchangeRateCreate"))
);
// FEE
const FeeList = Loadable(lazy(() => import("../pages/dashboard/FeeList")));

const FeeCreate = Loadable(lazy(() => import("../pages/dashboard/FeeCreate")));

// USER COMMISSION
const UserCommissionList = Loadable(
  lazy(() => import("../pages/dashboard/UserCommissionList"))
);

const UserCommissionCreate = Loadable(
  lazy(() => import("../pages/dashboard/UserCommissionCreate"))
);

// LIMIT TRANSACTION GENERALE
const LimitTransactionGeneraleList = Loadable(
  lazy(() => import("../pages/dashboard/LimitTransactionGeneraleList"))
);

const LimitTransactionGeneraleCreate = Loadable(
  lazy(() => import("../pages/dashboard/LimitTransactionGeneraleCreate"))
);

// SUPERVISOR
const SupervisorList = Loadable(
  lazy(() => import("../pages/dashboard/SupervisorList"))
);

const SupervisorCreate = Loadable(
  lazy(() => import("../pages/dashboard/SupervisorCreate"))
);

// AGENT
const AgentList = Loadable(lazy(() => import("../pages/dashboard/AgentList")));

const AgentCreate = Loadable(
  lazy(() => import("../pages/dashboard/AgentCreate"))
);
// CUSTOMER
const CustomerList = Loadable(
  lazy(() => import("../pages/dashboard/CustomerList"))
);

const CustomerCreate = Loadable(
  lazy(() => import("../pages/dashboard/CustomerCreate"))
);

// USER
const UserAccount = Loadable(
  lazy(() => import("../pages/dashboard/UserAccount"))
);
// MAIN
const PolitiqueConf = Loadable(lazy(() => import("../pages/PolitiqueConf")));
const TermesConditions = Loadable(
  lazy(() => import("../pages/TermesConditions"))
);
const PolitiqueSecurite = Loadable(
  lazy(() => import("../pages/PolitiqueSecurite"))
);
const ComingSoon = Loadable(lazy(() => import("../pages/ComingSoon")));
const Maintenance = Loadable(lazy(() => import("../pages/Maintenance")));
const Page500 = Loadable(lazy(() => import("../pages/Page500")));
const NotFound = Loadable(lazy(() => import("../pages/Page404")));
